/* Reading and writing a single scaffold label's line inside a questionnaire
 * answer.
 *
 * The care plan scaffolds are literally `Label: <br>` runs, so a label owns
 * exactly the text between its colon and the next line break. An atomic
 * suggestion targets one label, so accepting it has to rewrite that one line
 * and leave every other line — including the labels a sibling atom targets —
 * exactly as stored.
 *
 * Several scaffolds repeat a label under different sub-headings, so a label
 * alone does not address a line. An optional `section` narrows the search to
 * the lines between that heading and the next one; without it the first match
 * across the whole answer wins.
 *
 * Line identity comes from `toAnswerLines`, the same split the rendered delta
 * uses, so a card's diff and its write agree on what a line is. The write path
 * re-walks the DOM because that split is lossy: it discards the block
 * boundaries a rewrite has to put back, and the heading tag a section boundary
 * is read from. */

import {
  answerLineText,
  ANSWER_LINE_BREAK as BR,
  findLabelLine as findLabelLineIn,
  isHeadingTag,
  isLineBearingTag,
  LINE_BEARING_TAGS,
  matchesLabel as matchesLabelIn,
  sectionScope as sectionScopeIn,
  toAnswerHeadings,
} from '@seen/core/models/care-plans/answerScaffold'
import { toAnswerLines } from '#src/components/care-plan/answerDelta.ts'
import { parseAnswerHtml } from '#src/components/care-plan/parseAnswerHtml.ts'

function answerBody(html: string): HTMLElement {
  return new DOMParser().parseFromString(html, 'text/html').body
}

function lineText(html: string): string {
  return answerLineText(html, parseAnswerHtml)
}

function matchesLabel(lineHtml: string, label: string): boolean {
  return matchesLabelIn(lineHtml, label, parseAnswerHtml)
}

interface AnswerLineBlock {
  node: ChildNode
  element: Element | null
  // This block's lines, in the order and count `toAnswerLines` gives them.
  lines: string[]
  // Where `lines[0]` sits among the whole answer's lines.
  start: number
  // A `<br>` run, so a rewrite has to reassemble the run rather than the node.
  lineBearing: boolean
  headingBlock: boolean
}

/* The answer's top-level nodes carrying the line indices `toAnswerLines`
 * assigns them, so a line index resolves back to the node that has to be
 * rewritten. The skip rules mirror that split exactly; drifting from them would
 * shift every index a caller holds. */
function toLineBlocks(body: HTMLElement): AnswerLineBlock[] {
  const blocks: AnswerLineBlock[] = []
  let start = 0
  for (const node of Array.from(body.childNodes)) {
    const element = node instanceof Element ? node : null
    const lineBearing = element !== null && isLineBearingTag(element.tagName)
    let lines: string[]
    if (element === null) {
      const text =
        node.nodeType === Node.TEXT_NODE ? (node.textContent ?? '') : ''
      if (!text.trim()) {
        continue
      }
      lines = [text]
    } else if (lineBearing) {
      lines = element.innerHTML.split(BR)
    } else {
      lines = [element.outerHTML]
    }
    blocks.push({
      node,
      element,
      lines,
      start,
      lineBearing,
      headingBlock: element !== null && isHeadingTag(element.tagName),
    })
    start += lines.length
  }
  return blocks
}

function blockAtLine(
  blocks: AnswerLineBlock[],
  index: number
): AnswerLineBlock | null {
  return (
    blocks.find(
      (block) =>
        index >= block.start && index < block.start + block.lines.length
    ) ?? null
  )
}

/* Which lines sit inside a heading block, indexed exactly as `toAnswerLines`
 * indexes them. That split is flat: it hands back an `<h1>`–`<h4>`'s inner html
 * and discards the tag, so a renderer drawing every line as an equal sibling
 * shows a block title as body text — indistinguishable from the ordinary
 * paragraph under it. Reported alongside the split rather than folded into it,
 * because a line index is what `findLabelLine` returns and what a substitution
 * rewrites — the two must keep addressing the same lines.
 *
 * A bold line is not reported: its bold lives inside the line's own html, so it
 * already renders as the item's form field renders it. */
export function answerHeadingLines(html: string): boolean[] {
  const count = toAnswerLines(html).length
  const headings = new Array<boolean>(count).fill(false)
  for (const block of toLineBlocks(answerBody(html))) {
    if (!block.headingBlock) {
      continue
    }
    for (const offset of block.lines.keys()) {
      // A line past the split's end is a trailing blank the split dropped.
      if (block.start + offset < count) {
        headings[block.start + offset] = true
      }
    }
  }
  return headings
}

/* Locates the scaffold line whose label matches. Delegates to the shared model:
 * the index it returns is what a substitution below rewrites, so the lookup the
 * server enumerates against and the lookup a write uses must be one function. */
export function findLabelLine(
  html: string,
  label: string,
  section?: string
): number | null {
  return findLabelLineIn(html, label, section, parseAnswerHtml)
}

/* The label as the ANSWER spells it, read off the line it addresses. A card
 * renders this in front of its proposal, so the line still reads as one label
 * and one value; the answer's spelling wins over the atom's copy of it. Null
 * when the answer has no such line. */
export function labelLineHead(
  html: string,
  label: string,
  section?: string
): string | null {
  const index = findLabelLine(html, label, section)
  if (index === null) {
    return null
  }
  const text = lineText(toAnswerLines(html)[index] ?? '').trim()
  return text.slice(0, label.trim().length)
}

// The value currently written against `label`, with the label itself stripped —
// what a card diffs its proposal against. Null when the answer has no such line.
export function labelLineValue(
  html: string,
  label: string,
  section?: string
): string | null {
  const index = findLabelLine(html, label, section)
  if (index === null) {
    return null
  }
  const text = lineText(toAnswerLines(html)[index] ?? '').trim()
  return text.slice(label.trim().length).trim()
}

/* Cuts `parent` down to the first `remaining` characters of its rendered text,
 * dropping every node past that point and keeping the elements those characters
 * sit inside. Returns what is left of the budget, which is how a recursive call
 * tells its caller how much of the line the descent consumed. */
function truncateToTextLength(parent: ParentNode, remaining: number): number {
  let budget = remaining
  for (const node of Array.from(parent.childNodes)) {
    if (budget <= 0) {
      node.parentNode?.removeChild(node)
      continue
    }
    if (node instanceof Element) {
      budget = truncateToTextLength(node, budget)
      continue
    }
    const text = node.textContent ?? ''
    if (text.length > budget) {
      node.textContent = text.slice(0, budget)
    }
    budget -= Math.min(text.length, budget)
  }
  return budget
}

/* One line rewritten to hold `value`, keeping the label spelled — and marked up
 * — as the answer spells it. The scaffolds bold their labels, so an accept that
 * rebuilt the line from the plain label text would silently unbold every line it
 * touched.
 *
 * The head is therefore cut in the DOM rather than in the string. The label's
 * characters end INSIDE a `<strong>`, so slicing the markup at a rendered-text
 * offset cuts the tag in half — the reason this used to give up and rebuild.
 * Truncating the text node the label ends in and dropping everything after it
 * leaves whatever wrapped the label still wrapping it, and still closed. */
function withLabelValue(
  lineHtml: string,
  label: string,
  value: string
): string {
  const trimmedLabel = label.trim()
  const start = lineText(lineHtml)
    .toLowerCase()
    .indexOf(trimmedLabel.toLowerCase())
  if (start === -1) {
    return `${trimmedLabel} ${value}`
  }
  const body = answerBody(lineHtml)
  truncateToTextLength(body, start + trimmedLabel.length)
  return `${body.innerHTML} ${value}`
}

/* The tags a value's content is unwrapped out of when it is flattened. The
 * line-bearing set is what the answer's own line model counts; a list carries no
 * line of its own, but leaving one wrapped would nest a block in the run exactly
 * as a `<p>` does. */
const UNWRAPPED_BLOCK_TAGS = new Set([
  ...LINE_BEARING_TAGS,
  'UL',
  'OL',
  'LI',
  'BLOCKQUOTE',
])

function isUnwrappedBlock(node: ChildNode): node is Element {
  return node instanceof Element && UNWRAPPED_BLOCK_TAGS.has(node.tagName)
}

/* One run per block `parent` holds, with anything between blocks forming a run
 * of its own. Each run is serialized through a holder element rather than
 * concatenated, so text keeps the escaping it was parsed with. */
function toInlineRuns(parent: ParentNode, document: Document): string[] {
  const runs: string[] = []
  let pending: ChildNode[] = []
  const flush = (): void => {
    if (pending.length === 0) {
      return
    }
    const holder = document.createElement('div')
    for (const node of pending) {
      holder.appendChild(node.cloneNode(true))
    }
    runs.push(holder.innerHTML)
    pending = []
  }
  for (const node of Array.from(parent.childNodes)) {
    if (isUnwrappedBlock(node)) {
      flush()
      runs.push(...toInlineRuns(node, document))
    } else {
      pending.push(node)
    }
  }
  flush()
  return runs
}

/* A value flattened to inline html, each block it carried becoming a line of
 * the run. A line is `<br>`-separated inline html, so a value holding a block —
 * which an island editor's HTML always does, and which the propose tool's schema
 * invites the model to send — would otherwise be written INSIDE the line's own
 * paragraph, splitting the answer into more lines than it has and leaving the
 * label's line unreadable to every path that addresses it.
 *
 * A blank block is dropped rather than kept as an empty line, so a trailing
 * paragraph the editor left behind adds no `<br>`. A value already inline is
 * returned untouched. */
export function toInlineValue(html: string): string {
  const document = new DOMParser().parseFromString(html, 'text/html')
  if (!Array.from(document.body.childNodes).some(isUnwrappedBlock)) {
    return html
  }
  return toInlineRuns(document.body, document)
    .filter((run) => lineText(run).trim() !== '')
    .join('<br>')
}

/* Writes `value` into `label`'s line of `html`, preserving every other line,
 * restricted to `section` when the answer holds that heading.
 *
 * Null when the answer holds no such line — no line carries the label, or the
 * section asked for is not in the answer. That is a refusal to place the value
 * rather than an error. The line the value belongs on is part of the scaffold;
 * an answer without it is not the document this write was proposed against, so
 * it has been reshaped or the label was never one of its fields. Appending the
 * label as a new line is what stacked a filled `Goal:` under a blank one, and
 * any other recovery guesses at a place the scaffold does not offer, so the
 * value is not written at all and the caller reports it unplaced. */
export function substituteLabelValue(
  html: string,
  label: string,
  value: string,
  section?: string
): string | null {
  const document = new DOMParser().parseFromString(html, 'text/html')
  const body = document.body
  const blocks = toLineBlocks(body)
  /* Headings and section scope come from the shared model, so this live-DOM
   * walk and the server's enumeration read the same structure. */
  const headings = toAnswerHeadings(html, parseAnswerHtml)
  const scope = sectionScopeIn(headings, toAnswerLines(html).length, section)
  if (scope.kind === 'missing') {
    return null
  }
  const headingIndices = new Set(headings.map((heading) => heading.index))
  const start = scope.kind === 'section' ? scope.span.start : 0
  const end =
    scope.kind === 'section' ? scope.span.end : Number.POSITIVE_INFINITY

  for (const block of blocks) {
    for (const [offset, line] of block.lines.entries()) {
      const index = block.start + offset
      if (index < start || index >= end || headingIndices.has(index)) {
        continue
      }
      if (!matchesLabel(line, label)) {
        continue
      }
      if (block.lineBearing && block.element !== null) {
        const parts = [...block.lines]
        parts[offset] = withLabelValue(line, label, value)
        block.element.innerHTML = parts.join('<br>')
        return body.innerHTML
      }
      // A line that is not a `<br>` run is replaced whole, wrapped so the result
      // stays block-structured like the rest of the answer.
      const paragraph = document.createElement('p')
      paragraph.innerHTML = withLabelValue(line, label, value)
      body.replaceChild(paragraph, block.node)
      return body.innerHTML
    }
  }

  return null
}

/* Writes `value` over the line at `index`, leaving every other line as stored —
 * the write behind a clinician editing one rendered line by hand.
 *
 * Addressed by index rather than by label because the line is whatever they
 * clicked: a bold sub-heading, a sentence carrying no label at all, and a label
 * whose spelling repeats inside its own section are all lines a label lookup
 * cannot single out.
 *
 * Null when `index` no longer holds `expected`. The surface reads its lines from
 * the answer as it stood when it rendered, and an atom accepted since then has
 * rewritten that answer, so the index can address text the clinician never
 * opened; refusing leaves the edit unwritten rather than landing it on another
 * line. */
export function substituteLineValue(
  html: string,
  index: number,
  expected: string,
  value: string
): string | null {
  const document = new DOMParser().parseFromString(html, 'text/html')
  const body = document.body
  const block = blockAtLine(toLineBlocks(body), index)
  if (block === null) {
    return null
  }
  const offset = index - block.start
  if (block.lines[offset] !== expected) {
    return null
  }
  if (block.lineBearing && block.element !== null) {
    const parts = [...block.lines]
    parts[offset] = value
    block.element.innerHTML = parts.join('<br>')
    return body.innerHTML
  }
  // Same as a labelled substitution: a line that is not a `<br>` run is replaced
  // whole, wrapped so the result stays block-structured like the rest.
  const paragraph = document.createElement('p')
  paragraph.innerHTML = value
  body.replaceChild(paragraph, block.node)
  return body.innerHTML
}
